/**
 * Master defaults for the ZiBuzz frontend.
 *
 * This module contains only serializable constants (no functions on exported config).
 * Organized by feature/page for better maintainability.
 *
 * @example
 * import { SUBSCRIPTION_CONFIG } from '@/config/zibuzzConfig';
 */

const SUBSCRIPTION_LABELS = {
  OVERVIEW: {
    title: "Subscription",
    currentPlan: "Current Plan",
    expiry: "Expiry",
    teamSize: "Team Size",
    subscribeToPlan: "Subscribe to a Plan",
    renewSubscription: "Renew Subscription",
    upgrade: "Upgrade",
    renewChange: "Renew / Change",
    manageTeamSize: "Manage Team Size",
    planExpiringSoon: "Your plan is expiring soon!",
    planExpired: "Your plan has expired",
    activateTrial: "Activate 7-Day Trial",
  },
  RENEW: {
    title: "Choose a Plan",
    titleRenew: "Renew your Plan",
    backButton: "Back",
    planLabel: "Plan",
    validityLabel: "Validity",
    subscribeAndPay: "Subscribe & Pay",
    renewAndPay: "Renew & Pay",
    proceedToPay: "Proceed to Pay",
    cancel: "Cancel",
    currentPlanNote: "Your current plan ({plan}) will be renewed.",
  },
  CONFIRM: {
    title: "Confirm Subscription",
    description: "You are subscribing to {plan} ({validity}). You will be redirected to the payment gateway.",
    total: "Total: {amount}",
  },
  TOASTS: {
    selectPlan: "Please select a plan.",
    paymentSessionMissing: "Missing payment session. Please try again.",
    paymentCheckoutFailed: "Payment checkout failed.",
    paymentVerifyFailed: "Payment could not be verified.",
    subscriptionUpdated: "Subscription updated successfully!",
    loggedInSuccess: "Logged in successfully.",
    requestFailed: "Could not process request. Please try again.",
  },
  PRICING: {
    monthlyPrice: "Monthly Price",
    payToday: "Pay today",
    includedFeatures: "Included Features:",
    loginToSubscribe: "Login to Subscribe",
    getStarted: "Get Started",
  },
};

/**
 * Subscription feature configuration
 */
export const SUBSCRIPTION_CONFIG = {
  LABELS: SUBSCRIPTION_LABELS,

  /**
   * Number of days before expiry to show renewal option.
   * Set to 7 by default (show renewal when within 7 days of expiry).
   */
  ALLOW_RENEWAL_WINDOW_DAYS: 7,

  /**
   * Number of trial days for new users.
   * Set to 7 by default.
   */
  TRIAL_DAYS: 7,

  /**
   * Default plan for trial users when subscribing.
   */
  DEFAULT_PLAN_WHEN_TRIAL: "solo",

  /**
   * Cashfree payment gateway mode.
   * Options: "sandbox" | "production"
   */
  PAYMENT_MODE: process.env.REACT_APP_DEPLOY_ENV === 'Prod' ? 'production' : 'sandbox',
};

/**
 * Subscription page (public) configuration
 */
export const SUBSCRIPTION_PAGE_CONFIG = {
  LABELS: {
    title: "Choose Your Plan",
    subtitle: "Simple pricing for creators & businesses",
    authWarning: "Please {login} or {register} to subscribe to a plan",
    loginLink: "login",
    registerLink: "register",
    billingCycle: "Billing Cycle",
    savingsMessage: "Save {discount}% with {cycle} billing",
  },

  /**
   * Display options
   */
  SHOW_BADGES: true,
  SHOW_FEATURES: true,
  SHOW_PAY_TODAY: true,
};

/**
 * Authentication page labels
 */
export const AUTH_CONFIG = {
  LOGIN: {
    title: "Login to ZiBuzz",
    submitButton: "LOGIN",
    validatingButton: "Validating...",
    newUserLink: "New user?",
    registerLink: "Register",
    forgotPasswordLink: "Forgot Password?",
    resetPasswordLink: "Reset Password",
    mobileLabel: "Mobile (WhatsApp number)",
    passwordLabel: "Password",
    roleLabel: "Role",
    adminRole: "Admin",
    teamMemberRole: "Team member",
  },
  REGISTER: {
    title: "Register for ZiBuzz",
    submitButton: "Register",
    cancelButton: "Cancel",
    loginLink: "Already have an account?",
  },
  RESET_PASSWORD: {
    title: "Reset Password",
    submitButton: "Reset Password",
    cancelButton: "Cancel",
  },
};

/**
 * Chat application configuration
 */
export const CHAT_CONFIG = {
  MOBILE_BREAKPOINT: 700, // px
  SIDEBAR: {
    liveToggleLabel: "Live",
    archiveToggleLabel: "Archive",
  },
  UNREAD: {
    clearOnView: true,
    showBadges: true,
  },
};

/**
 * Team management configuration
 */
export const TEAM_CONFIG = {
  MAX_TEAM_SIZE: 10,
  MIN_TEAM_SIZE: 1,
  LABELS: {
    title: "Team Management",
    addMember: "Add Member",
    removeMember: "Remove Member",
    updateSize: "Update Team Size",
  },
};

/**
 * Barrel export for app-wide config.
 * Import specific configs as needed.
 */
export const ZIBUZZ = {
  SUBSCRIPTION: SUBSCRIPTION_CONFIG,
  SUBSCRIPTION_PAGE: SUBSCRIPTION_PAGE_CONFIG,
  AUTH: AUTH_CONFIG,
  CHAT: CHAT_CONFIG,
  TEAM: TEAM_CONFIG,
};
